import { NullableDateInput, stringifyDate } from '@superdispatch/dates';
import { z } from 'zod';
import {
  CanadaProvince,
  CanadaProvinceOptionsSet,
} from 'shared/form/CanadaProvince';
import { Schema, StringSchema } from 'yup';
import {
  CountryFieldOption,
  countryFieldOptions,
} from 'shared/form/CountryFieldOptions';
import { USAState, USAStateOptions } from 'shared/form/USAState';
import {
  yupBoolean,
  yupDateString,
  yupEnum,
  yupNumber,
  yupObject,
  yupString,
} from 'shared/utils/YupUtils';

export const CARRIER_LOGISTICS = [
  'auto_hauler',
  'towing',
  'driveaway',
] as const;

const carrierAddressYupSchema = yupObject({
  address: yupString().max(200).required('Enter Address'),
  city: yupString().max(100).required('Enter City'),
  zip: yupString()
    .max(10)
    .when('country', (country: CountryFieldOption, schema: StringSchema) => {
      if (country === 'CA') {
        return schema.required('Enter Postal Code');
      }
      return schema.required('Enter Zip');
    }),
  country: yupEnum([null, ...countryFieldOptions.keys()]).required(
    'Select Country',
  ),
  state: yupString().when(
    'country',
    (country: CountryFieldOption, schema: StringSchema) => {
      if (country === 'US') {
        return schema.test('us-state', 'Select valid state', (state) =>
          USAStateOptions.has(state as USAState),
        );
      }

      if (country === 'CA') {
        return schema.test('ca-province', 'Select valid province', (state) =>
          CanadaProvinceOptionsSet.has(state as CanadaProvince),
        );
      }

      return schema;
    },
  ),
});

const carrierInfoYupObjectSchema = carrierAddressYupSchema.concat(
  yupObject({
    contact_name: yupString().required(),
    email: yupString().email().required(),
    phone_numbers: yupString().required(),
    usdot_number: yupString(),
    name: yupString(),
    logo_url: yupString(),
    about: yupString(),
    mc_number: yupString(),
    website: yupString(),
    largest_trailer_capacity: yupNumber()
      .min(1, 'Trailer capacity can not be less than 1')
      .max(11, 'Trailer capacity can not be more than 11'),
    fmcsa_phone_number: yupObject({
      phone_number: yupString(),
      is_verified: yupBoolean(),
      grace_period_ends_at: yupDateString('DateTimeISO'),
    }).nullable(),
    logistics_type: yupEnum(CARRIER_LOGISTICS, null).required(),
  }),
);

function toStringValue(value: unknown): unknown {
  if (value == null) {
    return '';
  }

  return typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : value;
}

function toNullableNumber(value: unknown): unknown {
  return value == null || value === '' ? null : Number(value);
}

function toDateTimeISO(value: unknown): unknown {
  return (
    stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' }) ?? null
  );
}

function toCountry(value: unknown): null | CountryFieldOption {
  return countryFieldOptions.has(value as CountryFieldOption)
    ? (value as CountryFieldOption)
    : null;
}

const carrierInfoFields = z.object({
  address: z.preprocess(toStringValue, z.string().catch('')),
  city: z.preprocess(toStringValue, z.string().catch('')),
  zip: z.preprocess(toStringValue, z.string().catch('')),
  country: z.unknown().optional().transform(toCountry),
  state: z.preprocess(toStringValue, z.string().catch('')),
  contact_name: z.preprocess(toStringValue, z.string().catch('')),
  email: z.preprocess(toStringValue, z.string().catch('')),
  phone_numbers: z.preprocess(toStringValue, z.string().catch('')),
  usdot_number: z.preprocess(toStringValue, z.string().catch('')),
  name: z.preprocess(toStringValue, z.string().catch('')),
  logo_url: z.preprocess(toStringValue, z.string().catch('')),
  about: z.preprocess(toStringValue, z.string().catch('')),
  mc_number: z.preprocess(toStringValue, z.string().catch('')),
  website: z.preprocess(toStringValue, z.string().catch('')),
  largest_trailer_capacity: z.preprocess(
    toNullableNumber,
    z.number().nullable().catch(null),
  ),
  fmcsa_phone_number: z
    .object({
      phone_number: z.preprocess(toStringValue, z.string().catch('')),
      is_verified: z.boolean().nullable().catch(false),
      grace_period_ends_at: z.preprocess(
        toDateTimeISO,
        z.string().nullable().catch(null),
      ),
    })
    .nullable()
    .catch(null)
    .prefault({
      phone_number: '',
      is_verified: false,
      grace_period_ends_at: null,
    }),
  logistics_type: z.enum(CARRIER_LOGISTICS).nullable().catch(null),
});

export type CarrierInfoDTO = z.infer<typeof carrierInfoSchema>;
export const carrierInfoSchema = carrierInfoFields.superRefine(
  (values, context) => {
    function addIssue(path: string, message: string) {
      context.addIssue({ code: 'custom', path: [path], message });
    }

    if (!values.address) {
      addIssue('address', 'Enter Address');
    } else if (values.address.length > 200) {
      addIssue('address', 'Maximum length is 200 characters');
    }

    if (!values.city) {
      addIssue('city', 'Enter City');
    } else if (values.city.length > 100) {
      addIssue('city', 'Maximum length is 100 characters');
    }

    if (!values.zip) {
      addIssue(
        'zip',
        values.country === 'CA' ? 'Enter Postal Code' : 'Enter Zip',
      );
    } else if (values.zip.length > 10) {
      addIssue('zip', 'Maximum length is 10 characters');
    }

    if (!values.country) {
      addIssue('country', 'Select Country');
    }

    if (
      values.country === 'US' &&
      !USAStateOptions.has(values.state as USAState)
    ) {
      addIssue('state', 'Select valid state');
    }

    if (
      values.country === 'CA' &&
      !CanadaProvinceOptionsSet.has(values.state as CanadaProvince)
    ) {
      addIssue('state', 'Select valid province');
    }

    if (!values.contact_name) {
      addIssue('contact_name', 'This field is required');
    }

    if (!values.email) {
      addIssue('email', 'This field is required');
    } else if (!z.email().safeParse(values.email).success) {
      addIssue('email', 'Enter a valid email');
    }

    if (!values.phone_numbers) {
      addIssue('phone_numbers', 'This field is required');
    }

    if (values.largest_trailer_capacity != null) {
      if (values.largest_trailer_capacity < 1) {
        addIssue(
          'largest_trailer_capacity',
          'Trailer capacity can not be less than 1',
        );
      } else if (values.largest_trailer_capacity > 11) {
        addIssue(
          'largest_trailer_capacity',
          'Trailer capacity can not be more than 11',
        );
      }
    }

    if (!values.logistics_type) {
      addIssue('logistics_type', 'This field is required');
    }
  },
);

export const carrierInfoYupSchema =
  carrierInfoYupObjectSchema as unknown as Schema<CarrierInfoDTO>;
